import React from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { Button, Typography, Paper } from "@material-ui/core";
import { useUser } from "../../../context/userContext";
import pathConstants from "../../../constants/PathSimulation/pathSimulationConstants";
import { MACHINE_TYPES } from "../../../variables/machineVariables";
import StackVisualizer from "../../../components/StackVisualizer/StackVisualizer";
import TapeVisualizer from "../../../components/TapeVisualizer/TapeVisualizer";
import { isNullOrUndefined } from "../../../utils/generalValidators";

export default function PathHistoryDialog({
  isActive,
  onClose,
  historyList,
  automatonType,
}) {
  const { language } = useUser();

  const isTuringLike =
    automatonType === MACHINE_TYPES.ALL || automatonType === MACHINE_TYPES.MT;

  let dialogMaxWidth = "sm";
  if (isTuringLike) {
    dialogMaxWidth = "md";
  }

  return (
    <Dialog
      open={isActive}
      onClose={onClose}
      maxWidth={dialogMaxWidth}
      fullWidth
    >
      <DialogTitle>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">
            {pathConstants.pathSimulation.historyDialog.title[language]}
          </Typography>
          <Button
            onClick={onClose}
            color="secondary"
            variant="outlined"
            size="small"
          >
            {pathConstants.pathSimulation.historyDialog.close[language]}
          </Button>
        </div>
      </DialogTitle>
      <DialogContent dividers style={{ maxHeight: "60vh" }}>
        <List>
          {historyList.map((step, index) => {
            if (
              isNullOrUndefined(step) ||
              isNullOrUndefined(step.actualState) ||
              typeof step.actualState.name !== "string"
            ) {
              return null;
            }

            const isInitialStep = index === 0;
            const isLastStep = index === historyList.length - 1;
            const stepName = step.actualState.name;

            let currentStack = "[]";
            if (!isNullOrUndefined(step.stackContent)) {
              currentStack = step.stackContent;
            }

            let borderColor = "#2196f3";

            // Avaliação direta e limpa do status que vem do Backend
            if (isLastStep && step.status) {
              if (step.status === "ACCEPTED") {
                borderColor = "green";
              } else if (step.status === "REJECTED") {
                borderColor = "red";
              }
            }

            let consumedSymbols;
            let currentTransitionSymbol;
            let stepText;

            if (isInitialStep) {
              stepText = `${pathConstants.pathSimulation.steps.step[language]} ${index}: ${pathConstants.pathSimulation.steps.initialState[language]}: ${stepName}`;
              currentTransitionSymbol = "";
              consumedSymbols = "";
            } else {
              const previousStep = historyList[index - 1];

              if (step.pos === previousStep.pos) {
                currentTransitionSymbol = "λ";
                consumedSymbols = previousStep.symbol;
              } else {
                let accumulated = step.symbol || "";
                if (accumulated.length > 0) {
                  currentTransitionSymbol = accumulated.charAt(
                    accumulated.length - 1
                  );
                } else {
                  currentTransitionSymbol = "";
                }
                consumedSymbols = accumulated;
              }

              if (
                currentTransitionSymbol === "" ||
                isNullOrUndefined(currentTransitionSymbol)
              ) {
                currentTransitionSymbol = "λ";
              }

              const parentName = previousStep.actualState.name;

              let displayStepName = stepName;
              if (isLastStep) {
                displayStepName = `${stepName} (${pathConstants.pathSimulation.steps.current[language]})`;
              }

              stepText = `${pathConstants.pathSimulation.steps.step[language]} ${index}: ${parentName} → ${displayStepName}`;
            }

            let secondaryContent;

            if (isTuringLike) {
              secondaryContent = (
                <div style={{ marginTop: 8, width: "100%" }}>
                  <TapeVisualizer
                    tapeContent={step.currentTapeContent}
                    headPosition={step.headPosition}
                  />
                  <Typography
                    variant="body2"
                    align="right"
                    style={{ marginTop: 4 }}
                  ></Typography>
                </div>
              );
            } else {
              secondaryContent = (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginTop: "8px",
                  }}
                >
                  <div>
                    <Typography
                      variant="body2"
                      color="textPrimary"
                      style={{ textTransform: "none", display: "block" }}
                    >
                      {
                        pathConstants.pathSimulation.constants.readSymbol[
                          language
                        ]
                      }
                      :
                      <span style={{ fontWeight: "bold" }}>
                        {" "}
                        {currentTransitionSymbol}
                      </span>
                    </Typography>

                    <Typography
                      variant="body2"
                      color="textPrimary"
                      style={{
                        textTransform: "none",
                        display: "block",
                        marginTop: 4,
                      }}
                    >
                      {
                        pathConstants.pathSimulation.constants.consumedSymbols[
                          language
                        ]
                      }
                      :
                      <span style={{ fontWeight: "bold" }}>
                        {" "}
                        {consumedSymbols}
                      </span>
                    </Typography>
                  </div>

                  {automatonType === MACHINE_TYPES.AP && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        minWidth: "60px",
                        marginLeft: "10px",
                      }}
                    >
                      <Typography
                        variant="caption"
                        style={{ fontWeight: "bold", marginBottom: 2 }}
                      >
                        {pathConstants.pathSimulation.constants.stack[language]}
                        :
                      </Typography>
                      <StackVisualizer content={currentStack} />
                    </div>
                  )}
                </div>
              );
            }

            return (
              <Paper
                key={index}
                style={{
                  marginBottom: 10,
                  padding: 10,
                  borderLeft: `5px solid ${borderColor}`,
                }}
              >
                <ListItem style={{ padding: 0 }}>
                  <ListItemText
                    component="div"
                    primary={
                      <Typography
                        variant="subtitle1"
                        style={{ fontWeight: "bold", textTransform: "none" }}
                      >
                        {stepText}
                      </Typography>
                    }
                    secondary={secondaryContent}
                  />
                </ListItem>
              </Paper>
            );
          })}
        </List>
      </DialogContent>
    </Dialog>
  );
}
